// frontend/src/features/ups/modals/InternalDriverFormModal.js
import React, { useEffect, useMemo, useState } from 'react';

const inputClass =
  'w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/20';
const labelClass = 'mb-1 block text-sm font-medium text-app-muted';
const primaryButtonClass =
  'rounded-xl bg-app-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60';
const secondaryButtonClass =
  'rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface disabled:cursor-not-allowed disabled:opacity-60';

// Safely extracts data if editing, or sets empty strings if creating
function initialForm(driver) {
  return {
    name: driver?.name || '',
    vehicle_registration_number: driver?.vehicle_registration_number || '',
    phone: driver?.phone || '',
    email: driver?.email || '',
  };
}

// Runs business logic checks before allowing submission
function validate(form) {
  const errors = {};

  // Clean data before validating
  const name = form.name.trim();
  const vrn = form.vehicle_registration_number.trim();
  const phone = form.phone.trim();
  const email = form.email.trim();

  // Required fields
  if (!name) errors.name = 'Name is required.';
  if (!vrn) errors.vehicle_registration_number = 'Vehicle registration number is required.';

  // Format checks
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (phone && phone.length > 50) {
    errors.phone = 'Phone number is too long.';
  }

  return errors;
}

export default function InternalDriverFormModal({
  isOpen,
  onClose,
  onSubmit,
  driver = null,
  submitting = false,
}) {
  const isEdit = Boolean(driver?.id);

  const [form, setForm] = useState(initialForm(driver));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setForm(initialForm(driver));
    setErrors({});
    setSubmitError('');
  }, [isOpen, driver]);

  const title = useMemo(
    () => (isEdit ? 'Edit Internal Driver' : 'New Internal Driver'),
    [isEdit]
  );

  if (!isOpen) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = validate(form);
    setErrors(nextErrors);
    setSubmitError('');

    if (Object.keys(nextErrors).length > 0) return;

    try {
      await onSubmit({
        name: form.name.trim(),
        vehicle_registration_number: form.vehicle_registration_number.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
      });
    } catch (err) {
      setSubmitError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to save internal driver.'
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close modal backdrop"
        className="absolute inset-0"
        onClick={() => {
          if (!submitting) onClose();
        }}
      />

      <div className="relative z-10 flex h-[85dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card text-app-text shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-2xl">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-app-border px-4 py-4 sm:px-5">
          <div>
            <h2 className="text-xl font-semibold text-app-text">{title}</h2>
            <p className="mt-1 text-sm text-app-muted">
              Company-owned internal driver record.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close internal driver modal"
            className="rounded-xl px-3 py-2 text-app-muted transition hover:bg-app-surface hover:text-app-text disabled:cursor-not-allowed disabled:opacity-60"
          >
            x
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
            {submitError && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {submitError}
              </div>
            )}

            <div>
              <label htmlFor="internal-driver-name" className={labelClass}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="internal-driver-name"
                name="name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                disabled={submitting}
                className={`${inputClass} ${
                  errors.name ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100' : ''
                }`}
                placeholder="Driver name"
              />
              {errors.name && (
                <div className="mt-1 text-xs text-red-600">{errors.name}</div>
              )}
            </div>

            <div>
              <label htmlFor="internal-driver-vrn" className={labelClass}>
                Vehicle Registration Number <span className="text-red-500">*</span>
              </label>
              <input
                id="internal-driver-vrn"
                name="vehicle_registration_number"
                type="text"
                value={form.vehicle_registration_number}
                onChange={(e) =>
                  setField('vehicle_registration_number', e.target.value.toUpperCase())
                }
                disabled={submitting}
                className={`${inputClass} ${
                  errors.vehicle_registration_number ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100' : ''
                }`}
                placeholder="AB12 CDE"
              />
              {errors.vehicle_registration_number && (
                <div className="mt-1 text-xs text-red-600">
                  {errors.vehicle_registration_number}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="internal-driver-phone" className={labelClass}>
                  Phone
                </label>
                <input
                  id="internal-driver-phone"
                  name="phone"
                  type="text"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  disabled={submitting}
                  className={`${inputClass} ${
                    errors.phone ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100' : ''
                  }`}
                  placeholder="07123 456789"
                />
                {errors.phone && (
                  <div className="mt-1 text-xs text-red-600">{errors.phone}</div>
                )}
              </div>

              <div>
                <label htmlFor="internal-driver-email" className={labelClass}>
                  Email
                </label>
                <input
                  id="internal-driver-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  disabled={submitting}
                  className={`${inputClass} ${
                    errors.email ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100' : ''
                  }`}
                  placeholder="driver@example.com"
                />
                {errors.email && (
                  <div className="mt-1 text-xs text-red-600">{errors.email}</div>
                )}
              </div>
            </div>
          </div>

          <div
            className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border bg-app-surface px-4 py-3 sm:px-5 sm:py-4"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={secondaryButtonClass}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className={primaryButtonClass}
            >
              {submitting
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : isEdit
                ? 'Save changes'
                : 'Create internal driver'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
